import { findTextOnlyWriteProposalMessageId } from "@/components/assistant/detectTextOnlyWriteProposal";
import { WRITE_APPROVAL_AUTO_CONTINUE_MESSAGE_METADATA } from "@/components/assistant/hiddenChatUiMessages";
import type { UIMessage } from "ai";
import { useEffect, useRef } from "react";

type Args = {
  status: string;
  messages: UIMessage[];
  actionLoading: boolean;
  activePending: unknown;
  continuePrompt: string;
  messageTextContent: (message: UIMessage) => string;
  isWriteToolName: (toolName: string) => boolean;
  resolvedToolCallIds?: ReadonlySet<string>;
  sendMessage: (message: {
    text: string;
    metadata?: typeof WRITE_APPROVAL_AUTO_CONTINUE_MESSAGE_METADATA;
  }) => void | Promise<void>;
};

export function useAutoContinueTextOnlyWriteProposal({
  status,
  messages,
  actionLoading,
  activePending,
  continuePrompt,
  messageTextContent,
  isWriteToolName,
  resolvedToolCallIds,
  sendMessage,
}: Args): void {
  const autoContinueAttemptedForAssistantMessageIdRef = useRef(new Set<string>());

  useEffect(() => {
    if (status !== "ready" || actionLoading) return;
    if (activePending) return;
    const assistantMessageId = findTextOnlyWriteProposalMessageId({
      messages,
      messageTextContent,
      isWriteToolName,
      resolvedToolCallIds,
    });
    if (!assistantMessageId) return;
    if (autoContinueAttemptedForAssistantMessageIdRef.current.has(assistantMessageId)) {
      return;
    }
    autoContinueAttemptedForAssistantMessageIdRef.current.add(assistantMessageId);
    void sendMessage({
      text: continuePrompt,
      metadata: WRITE_APPROVAL_AUTO_CONTINUE_MESSAGE_METADATA,
    });
  }, [
    status,
    messages,
    actionLoading,
    activePending,
    continuePrompt,
    messageTextContent,
    isWriteToolName,
    resolvedToolCallIds,
    sendMessage,
  ]);
}
